import {classes, style} from './doppe-page-profile-name-view.st.css';
import React, {useMemo} from 'react';
import {DoppeHideableValueRenderer} from '../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {
    BymoPagePropsForViewer, doppeHideableValueIsHidden,
    doppePremiumFeatureIsEnabled,
    DoppePremiumFeatures,
    DoppeProfileNameAutoSource,
    useBymoPageProps
} from '../../client-server-common';
import {BymoPageContextProps, useBymoPageContextProps} from '../../doppe-sdk';
import {autoValueGetValue, evaluateFunction, Values} from '@wix/devzai-utils-common';
import {Icon} from '@wix/devzai-utils-react';
import {DoppeViewerAppIconVerifiedBadgeMedium} from '../doppe-viewer-icons/doppe-viewer-icons';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppePageProfilePicture} from '../doppe-page-profile-picture/doppe-page-profile-picture';

export const DoppePageProfileNameViewVerifiedCheckAppearance = {
    Inline: 'inline',
    Superscript: 'superscript',
    Hidden: 'hidden'
} as const;

export type DoppePageProfileNameViewVerifiedCheckAppearance = Values<typeof DoppePageProfileNameViewVerifiedCheckAppearance>;

export const DoppePageProfileNameView = React.memo(function DoppePageProfileNameView (props: DoppePageProfileNameView.Props) {
    const {
        defaultProfileNameAutoSource,
        verifiedCheckAppearance = DoppePageProfileNameViewVerifiedCheckAppearance.Inline,
        showProfileImage = false,

        className,
        ...htmlAttributes
    } = props;

    const bymoPageProps = useBymoPageProps();
    const bymoPageContextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();


    const isVerifiedEnabledFromPremium =  doppePremiumFeatureIsEnabled(
        bymoPageContextProps.premiumFeaturesStatus, DoppePremiumFeatures.hopp_verified_badge);

    const isRemoveBanner =  doppePremiumFeatureIsEnabled(
        bymoPageContextProps.premiumFeaturesStatus, DoppePremiumFeatures.hopp_remove_banner);


    const enableVerifiedAccountFeatures = pageViewerContext.mode === BymoPageViewerMode.Live ?
        bymoPageContextProps.viewerEnableVerifiedAccountFeatures || (isVerifiedEnabledFromPremium && isRemoveBanner) :
        true;

    const profilePictureLinkInBio = bymoPageProps.profilePictureLinkInBio;

    const isVerifiedEnabled = useMemo(() => {
        return verifiedCheckAppearance !== DoppePageProfileNameViewVerifiedCheckAppearance.Hidden && bymoPageProps.showVerifiedCheck && enableVerifiedAccountFeatures;
    }, [verifiedCheckAppearance, bymoPageProps.showVerifiedCheck, enableVerifiedAccountFeatures ])

    return (
        <DoppeHideableValueRenderer
            hideableValue={bymoPageProps.profileName}
            renderFunction={(profileName) => {

                const profileNameAutoSource = evaluateFunction(() => {
                    if (typeof profileName === 'string') {
                        return undefined;
                    } else if (profileName === null) {
                        return defaultProfileNameAutoSource;
                    } else {
                        return autoValueGetValue(profileName);
                    }
                })



                return (
                    bymoPageContextProps.userDisplayName ? (
                        <div
                            {...htmlAttributes}
                            className={style(
                                classes.root,
                                {
                                    isAccountName: profileNameAutoSource === DoppeProfileNameAutoSource.AccountName,
                                    verifiedCheckAppearance: verifiedCheckAppearance,
                                    isVerfiedEnabled: isVerifiedEnabled
                                },
                                className
                            )}
                        >
                            {
                                showProfileImage ?
                                    <DoppeHideableValueRenderer
                                        hideableValue={profilePictureLinkInBio}
                                        renderFunction={profilePicture => (
                                            <DoppePageProfilePicture
                                                className={style(classes.profile, {})}
                                                profilePicture={profilePicture}
                                                size={60}
                                            />
                                        )}
                                    />
                                    :
                                    null
                            }

                            <span
                                className={style(classes.text, {})}
                            >
                                {evaluateFunction(() => {

                                    if (typeof profileName === 'string') {
                                        return profileName;
                                    } else {
                                        const profileNameAutoSource = profileName === null ? defaultProfileNameAutoSource : autoValueGetValue(profileName);

                                        switch (profileNameAutoSource) {
                                            case DoppeProfileNameAutoSource.DisplayName: {
                                                return bymoPageContextProps.userDisplayName;
                                            }
                                            case DoppeProfileNameAutoSource.AccountName: {
                                                return `@${bymoPageContextProps.userAccountName}`
                                            }
                                        }
                                    }
                                })}
                            </span>
                            {isVerifiedEnabled ? (
                                <span
                                    className={style(classes.verifiedCheckSpan, {})}
                                >
                                       <Icon
                                           className={style(classes.verifiedCheck, {})}
                                           icon={DoppeViewerAppIconVerifiedBadgeMedium}
                                       />
                                </span>

                            ) : null}

                        </div>
                    ) : null
                )
            }}
        />
    );
});

export namespace DoppePageProfileNameView {

    export interface Props extends React.HTMLAttributes<any> {
        defaultProfileNameAutoSource: DoppeProfileNameAutoSource;
        verifiedCheckAppearance?: DoppePageProfileNameViewVerifiedCheckAppearance;
        showProfileImage?: boolean;
    }
}


export function getUserDisplayName(options: {
    bymoPageProps: BymoPagePropsForViewer;
    bymoPageContextProps: BymoPageContextProps;
    defaultProfileNameAutoSource: DoppeProfileNameAutoSource
}) {

    const {
        bymoPageProps,
        bymoPageContextProps,
        defaultProfileNameAutoSource
    } = options;

    if (!bymoPageContextProps.userDisplayName || doppeHideableValueIsHidden(bymoPageProps.profileName)) {
        return '';
    }

    if (typeof bymoPageProps.profileName === 'string') {
        return bymoPageProps.profileName;
    } else {
        const profileNameAutoSource = bymoPageProps.profileName === null ? defaultProfileNameAutoSource : autoValueGetValue(bymoPageProps.profileName);

        switch (profileNameAutoSource) {
            case DoppeProfileNameAutoSource.DisplayName: {
                return bymoPageContextProps.userDisplayName;
            }
            case DoppeProfileNameAutoSource.AccountName: {
                return `@${bymoPageContextProps.userAccountName}`
            }
        }
    }

}